import { useRef } from 'react';
import { istDateYmd } from '../../lib/format';

function ddmmyyyy(ymd: string): string {
  const [y, m, d] = ymd.split('-');
  return y && m && d ? `${d}/${m}/${y}` : ymd;
}

type Props = {
  value: string; // YYYY-MM-DD
  onChange: (v: string) => void;
  min?: string;
  max?: string;
};

/**
 * Confirmed-chip date trigger for the reading sheets. Shows the value (or
 * "Today" in the common case) on a button styled like the sheet's other fields;
 * tapping opens the OS date picker. The native <input type="date"> is kept as
 * the hidden mechanism so we reuse the platform calendar, but the field no
 * longer reads as a prominent "fill me" input.
 */
export function DateField({ value, onChange, min, max }: Props) {
  const ref = useRef<HTMLInputElement>(null);

  const display = !value
    ? 'Select a date'
    : value === istDateYmd(new Date())
      ? 'Today'
      : ddmmyyyy(value);

  const open = () => {
    const el = ref.current;
    if (!el) return;
    const picker = (el as unknown as { showPicker?: () => void }).showPicker;
    if (typeof picker === 'function') {
      try {
        picker.call(el);
        return;
      } catch {
        /* fall through to focus/click for older browsers */
      }
    }
    el.focus();
    el.click();
  };

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={open}
        aria-label={value ? `Change date, currently ${display}` : 'Pick a date'}
        className="w-full px-3 py-3 text-xl text-center font-mono bg-[var(--color-surface-soft)] border-2 border-[var(--color-border,#d1d5db)] rounded-2xl text-[var(--color-text)] outline-none active:border-[var(--green-700,#1B6A41)]"
      >
        {display}
      </button>
      <input
        ref={ref}
        type="date"
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value)}
        aria-hidden="true"
        tabIndex={-1}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, pointerEvents: 'none' }}
      />
    </div>
  );
}
